import { h, Component } from 'preact'
import { GraphTitle } from '../GraphTitle'
import { Loader } from '../Loader'
import { Bar } from 'react-chartjs-2'
import { displayLegendsWithMarginBottom, getBrandBlue, getBrandBorderGrey, getBrandYellow } from '../utils'
import { ChartData, ChartTooltipItem } from 'chart.js'
import AnalyticsDateFormatter from '../AnalyticsDateFormatter'
import axios from 'axios'
displayLegendsWithMarginBottom()

interface Props {
  shouldChartRedraw: boolean,
  startDate: Date,
  endDate: Date,
  selectedRoles: Array<string>,
  selectedCategories: Array<string>,
  selectedContentTypes: Array<string>
}

interface DateItem {
  time: string,
  count: number
}

interface State {
  isLoading: boolean,
  viewsOverTime: Array<DateItem>,
  uniqueUsersRaisedTickets: Array<DateItem>
}

class UniqueViewsOfHelpfulContentOverTime extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.setState({
      isLoading: true,
      viewsOverTime: [],
      uniqueUsersRaisedTickets: []
    })
  }

  async componentDidUpdate (prevProps: Props, prevState: State) {
    if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.selectedRoles !== prevProps.selectedRoles || this.props.selectedContentTypes !== prevProps.selectedContentTypes || this.props.selectedCategories !== prevProps.selectedCategories) {
      await this.fetchData()
    }
  }

  async fetchData () {
    this.setState({ isLoading: true })
    const [uniqueViewsOfHelpfulContent, uniqueUsersRaisedTickets] = await Promise.all([this.fetchUniqueViewsOfHelpfulContentOverTime(), this.fetchUniqueUsersRaisedTicketsOverTime()])
    this.setState({
      viewsOverTime: uniqueViewsOfHelpfulContent.data,
      uniqueUsersRaisedTickets: uniqueUsersRaisedTickets.data,
      isLoading: false
    })
  }
  async fetchUniqueViewsOfHelpfulContentOverTime () {
    const { startDate, endDate, selectedRoles, selectedCategories, selectedContentTypes } = this.props
    return axios.post('/analyticsV2/uniqueViewsOfHelpfulContentOverTime', {
      filters: {
        start: AnalyticsDateFormatter.formatDate(startDate),
        end: AnalyticsDateFormatter.formatDate(endDate, false),
        roles: selectedRoles,
        categories: selectedCategories,
        contentTypes: selectedContentTypes
      }
    })
  }
  async fetchUniqueUsersRaisedTicketsOverTime () {
    const { startDate, endDate } = this.props
    return axios.post('/analyticsV2/uniqueUsersRaisedTicketsOverTime', {
      filters: {
        start: AnalyticsDateFormatter.formatDate(startDate),
        end: AnalyticsDateFormatter.formatDate(endDate, false)
      }
    })
  }

  render ({ shouldChartRedraw }: Props, { isLoading, viewsOverTime, uniqueUsersRaisedTickets }: State) {
    return <div class='graph-row'>
      <GraphTitle customClass='with-legend'
        title='Self-service and helpdesk user trends'
        tooltipMessage='Unique visitors who viewed help content and unique users who created tickets over time.' />
      {isLoading ? <Loader /> : <Bar redraw={shouldChartRedraw} data={{
        datasets: [{
          label: 'Unique visitors who viewed help content',
          fill: false,
          data: viewsOverTime.map((d: DateItem) => {
            return { x: d.time, y: d.count }
          }),
          backgroundColor: getBrandBlue()
        },
        {
          label: 'Unique users that submit a ticket',
          fill: false,
          data: uniqueUsersRaisedTickets.map((d: DateItem) => {
            return { x: d.time, y: d.count }
          }),
          backgroundColor: getBrandYellow()
        }]
      }} options={{
        title: {
          display: false
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 24
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          intersect: false,
          mode: 'index',
          callbacks: {
            title (item: ChartTooltipItem[], data: ChartData): string | string[] {
              if (item.length > 0 && item[0].label) {
                const date = new Date(item[0].label.substr(0, 10))
                const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date)
                const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date)
                return `${month} ${day}`
              }
              return ''
            }
          }
        },
        legend: {
          display: true,
          position: 'top',
          align: 'start',
          labels: {
            boxWidth: 12
          }
        },
        scales: {
          xAxes: [{
            type: 'time',
            offset: true,
            time: {
              minUnit: 'day'
            },
            gridLines: {
              display: false
            },
            ticks: {
              maxRotation: 0,
              callback: (value, index, values) => {
                return '      ' + value + '      '
              }
            }
          }],
          yAxes: [{
            gridLines: {
              borderDash: [8, 4],
              color: getBrandBorderGrey(),
              drawBorder: false
            },
            ticks: {
              padding: 16,
              beginAtZero: true
            }
          }]
        }
      }} />}
    </div>
  }
}

export default UniqueViewsOfHelpfulContentOverTime
